/**
 * Orchestrator. Runs sniff() first for instant container/faststart feedback,
 * then invokes the full probe (via mediabunny — no wasm) for codec, bitrate,
 * color, and stream info, and merges.
 *
 * Callers that want the cheapest possible answer can stop at sniff(); callers
 * that need full analysis call probe() which does both.
 */

import { probeWithMediabunny } from './mediabunny-probe';
import { sniff, type SniffResult } from './sniff';
import type { VideoProbeReport } from './types';

export interface ProbeOptions {
  /** Skip the full pass and return sniff-only results. Default false. */
  sniffOnly?: boolean;
  /** Called when the sniff pass completes, before the full probe runs. */
  onSniffComplete?: (report: VideoProbeReport) => void;
}

export async function probe(input: File | Blob, options: ProbeOptions = {}): Promise<VideoProbeReport> {
  const sniffResult = await sniff(input);
  const sniffReport = sniffToReport(sniffResult);
  options.onSniffComplete?.(sniffReport);

  if (options.sniffOnly) return sniffReport;
  if (sniffResult.bailout === 'unsupported-container' && sniffResult.container.format !== 'webm') {
    return sniffReport;
  }

  const fullResult = await probeWithMediabunny(input);
  return mergeReports(sniffReport, fullResult);
}

export async function probeSniffOnly(input: File | Blob): Promise<VideoProbeReport> {
  return probe(input, { sniffOnly: true });
}

function sniffToReport(sniff: SniffResult): VideoProbeReport {
  return {
    schemaVersion: 1,
    container: sniff.container,
    video: null,
    audio: null,
    durationSec: 0,
    bailout: sniff.bailout,
    warnings: [...sniff.warnings],
    source: 'sniff',
  };
}

function mergeReports(sniffReport: VideoProbeReport, fullResult: Awaited<ReturnType<typeof probeWithMediabunny>>): VideoProbeReport {
  const mergedWarnings = [...sniffReport.warnings, ...fullResult.warnings];
  const bailout = sniffReport.bailout ?? fullResult.bailout;

  return {
    schemaVersion: 1,
    container: sniffReport.container,
    video: fullResult.video,
    audio: fullResult.audio,
    durationSec: fullResult.durationSec || sniffReport.durationSec,
    bailout,
    warnings: mergedWarnings,
    source: 'merged',
  };
}
